/* eslint-disable prefer-arrow-callback */
import { useCallback } from "react";

import { useNavigate } from "utils";

import { CurrencyCode } from "consts";

import { NO_RESILIENCE_SCORES } from "pages/app/scenario-analysis/hooks/useScenarioResilienceScores";
import { ScenarioData, ScenarioSettings } from "pages/app/scenario-analysis/types/scenario";
import { SoilSightOutcomesByScenario } from "pages/app/scenario-analysis/utils/forecast";
import { ScenarioPrintState } from "pages/app/scenario-analysis/utils/printState";
import { ScenarioResilienceByScenarioId } from "pages/app/scenario-analysis/utils/scenarioResilienceScore";

type ScenarioPdfExportArgs = {
  scenarios: ScenarioData[];
  soilSightOutcomes: SoilSightOutcomesByScenario | null;
  settings: ScenarioSettings;
  currency: CurrencyCode | null;
  resilienceData: ScenarioResilienceByScenarioId;
  isResilienceError: boolean;
  fieldName: string | null;
};

/** Returns a handler that opens the PDF report page with a snapshot of what the analysis page shows. */
export const useScenarioPdfExport = ({
  scenarios,
  soilSightOutcomes,
  settings,
  currency,
  resilienceData,
  isResilienceError,
  fieldName,
}: ScenarioPdfExportArgs) => {
  const navigate = useNavigate();

  // Snapshot the report needs, taken at click time; a failed refetch leaves the previous run's scores cached, so they're dropped.
  const handleDownloadPdf = useCallback(
    function handleDownloadPdf() {
      const printState: ScenarioPrintState = {
        scenarios,
        soilSightOutcomes,
        settings,
        currency,
        resilienceScores: isResilienceError ? NO_RESILIENCE_SCORES : resilienceData,
        fieldName,
      };
      navigate("/scenarios-pdf", { state: printState });
    },
    [currency, fieldName, isResilienceError, navigate, resilienceData, scenarios, settings, soilSightOutcomes]
  );

  return handleDownloadPdf;
};
